import { useState, useCallback, useRef, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  useClients, useRFP, useCatalogue, useMonths,
  useMonthAnalysis, useRangeAnalysis,
} from "./hooks/useApi";
import * as api from "./api/client";
import Login from "./pages/Login";
import AlertConfig from "./pages/AlertConfig";
import PrereqBanner from "./components/PrereqBanner";
import CompartmentAnalysis from "./pages/CompartmentAnalysis";
import ClientSettings from "./pages/ClientSettings";
import RFPBuilderV3 from "./pages/RFPBuilderV3";
import OnboardingGuide from "./pages/OnboardingGuide";
import AdminPanel  from "./pages/AdminPanel";
import {
  Spinner, ErrorBanner, SuccessBanner, Card, Badge,
  Empty, Btn, Input, Select, Table, TR, TD,
  SectionTitle, KPICard, AIBox,
} from "./components/UI";

const fmtINR = n => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits:2, maximumFractionDigits:2 })}`;
const fmt    = (n, d=0) => n == null ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits:d, maximumFractionDigits:d });

function downloadCSV(filename, headers, rows) {
  const escape = v => { if(v==null)return""; const s=String(v); return s.includes(",")||s.includes('"')||s.includes("\n")?`"${s.replace(/"/g,'""')}"`:s; };
  const lines = [headers.map(escape).join(","), ...rows.map(r=>r.map(escape).join(","))].join("\n");
  const blob = new Blob(["\uFEFF"+lines], {type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function ReadonlyBanner() {
  return (
    <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, padding:"8px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:10, fontSize:13, color:"#1E40AF" }}>
      <span style={{ fontSize:16 }}>👁</span>
      <span><strong>Read-only access</strong> — You can view and download reports but cannot make any changes.</span>
    </div>
  );
}

function Sidebar({ view, setView, clients, activeClientId, setActiveClientId }) {
  const { user, logout, isReadonly, isAdmin } = useAuth();
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");

  const addClient = async () => {
    if (!newName.trim()) return;
    await api.clients.create({ name: newName.trim(), region: "India", currency: "INR" });
    setShowNew(false); setNewName(""); window.location.reload();
  };

  const navItems = [
    { id:"dashboard",   label:"Dashboard",        icon:"◉" },
    { id:"analysis",    label:"Monthly analysis",  icon:"◈" },
    { id:"ingest",      label:"Data ingestion",    icon:"↑", adminOnly:true },
    { id:"rfp",         label:"RFP builder",       icon:"✎" },
    { id:"alerts",      label:"Cost alerts",       icon:"🔔" },
    { id:"compartments", label:"Compartment analysis", icon:"🏢" },
    { id:"settings", label:"Client settings", icon:"⚙", adminOnly:true },
    { id:"guide", label:"Onboarding guide", icon:"📋", adminOnly:true },
            { id:"catalogue",   label:"Price catalogue",   icon:"₹" },
  ];

  return (
    <div style={{ width:240, background:"#0F172A", display:"flex", flexDirection:"column", flexShrink:0, height:"100vh" }}>
      <div style={{ padding:"24px 20px 16px" }}>
        <div style={{ fontSize:10, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:4 }}>OCI Cost Intelligence</div>
        <div style={{ fontSize:18, fontWeight:800, color:"#F1F5F9", lineHeight:1.2, fontFamily:"'DM Sans',sans-serif" }}>Cloud Billing<br/>Platform</div>
      </div>

      <div style={{ padding:"8px 12px", margin:"0 8px 8px", background:"#1E293B", borderRadius:10 }}>
        <div style={{ fontSize:10, color:"#64748B", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Active client</div>
        {clients?.length > 0
          ? <select value={activeClientId||""} onChange={e=>setActiveClientId(e.target.value)}
              style={{ width:"100%", background:"#334155", color:"#F1F5F9", border:"1px solid #475569", borderRadius:6, padding:"7px 10px", fontSize:13, fontWeight:600 }}>
              <option value="" disabled>Select client…</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          : <div style={{ color:"#64748B", fontSize:12 }}>No clients yet</div>
        }
        {!isReadonly && (
          showNew
            ? <div style={{ marginTop:8, display:"flex", gap:6 }}>
                <input autoFocus value={newName} onChange={e=>setNewName(e.target.value)}
                  onKeyDown={e=>{ if(e.key==="Enter")addClient(); if(e.key==="Escape")setShowNew(false); }}
                  placeholder="Client name"
                  style={{ flex:1, background:"#1E293B", color:"#F1F5F9", border:"1px solid #475569", borderRadius:6, padding:"6px 10px", fontSize:12 }}/>
                <button onClick={addClient} style={{ background:"#22C55E", color:"#fff", border:"none", borderRadius:6, padding:"6px 10px", cursor:"pointer", fontSize:12, fontWeight:700 }}>✓</button>
              </div>
            : <button onClick={()=>setShowNew(true)} style={{ marginTop:8, width:"100%", background:"transparent", color:"#64748B", border:"1px dashed #334155", borderRadius:6, padding:"6px", cursor:"pointer", fontSize:12, fontWeight:600 }}>+ Add client</button>
        )}
      </div>

      <nav style={{ padding:"0 8px", flex:1 }}>
        {navItems.filter(n=>(!n.adminOnly||!isReadonly)&&(!n.hideForLimited||!isLimited)).map(n=>(
          <button key={n.id} onClick={()=>setView(n.id)} style={{
            width:"100%", padding:"10px 14px", marginBottom:2, borderRadius:8, border:"none", cursor:"pointer",
            display:"flex", alignItems:"center", gap:12, textAlign:"left",
            background: view===n.id ? "#1E40AF" : "transparent",
            color: view===n.id ? "#fff" : "#94A3B8",
            fontSize:13, fontWeight:view===n.id?700:500, transition:"all 0.15s",
            fontFamily:"'DM Sans',sans-serif",
          }}>
            <span style={{ fontSize:14, opacity:0.8 }}>{n.icon}</span>{n.label}
          </button>
        ))}
      </nav>

      <div style={{ padding:"12px 20px", borderTop:"1px solid #1E293B" }}>
        <div style={{ fontSize:11, color:"#475569" }}>
          <div style={{ fontWeight:600, color:"#64748B", marginBottom:2 }}>{user?.full_name||user?.email}</div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {isReadonly
              ? <span style={{ background:"#1E3A5F", color:"#93C5FD", padding:"1px 7px", borderRadius:4, fontSize:10, fontWeight:700 }}>READ ONLY</span>
              : <span style={{ color:"#475569" }}>{user?.is_admin?"Administrator":"Analyst"}</span>
            }
          </div>
        </div>
        {isAdmin && (
          <a href="/admin" style={{ display:"block", marginTop:8, background:"#1E293B", color:"#94A3B8", borderRadius:6, padding:"6px 10px", fontSize:11, fontWeight:600, textDecoration:"none", textAlign:"center" }}>
            ⚙ Admin panel
          </a>
        )}
        <button onClick={logout} style={{ marginTop:8, background:"none", border:"none", color:"#475569", fontSize:11, cursor:"pointer", padding:0 }}>
          Sign out →
        </button>
      </div>
    </div>
  );
}

function Dashboard({ clientId, clientName }) {
  const { isReadonly } = useAuth();
  const { data:months } = useMonths(clientId);
  const allMonths = months || [];
  const { data:summary, loading, error } = useRangeAnalysis(clientId, allMonths);

  const downloadDashboard = () => {
    if (!summary) return;
    const summaryHeaders = ["Month","Planned (INR)","Actual OCI (INR)","OCI Delta (INR)","Overage @ RFP (INR)","Status"];
    const summaryRows = summary.months.map(m=>[m.month,m.total_planned,m.total_actual,m.total_cost_delta,m.total_overage_rfp,m.status]);
    summaryRows.push(["GRAND TOTAL",summary.grand_planned,summary.grand_actual,summary.grand_cost_delta,summary.grand_overage_rfp,summary.grand_overage_rfp>0?"OVER":"WITHIN"]);
    downloadCSV(`${clientName}_Monthly_Summary.csv`, summaryHeaders, summaryRows);
    setTimeout(() => {
      const detailHeaders = ["Month","Service","Description","Unit","RFP Unit Price","Planned Qty","Actual Qty","Overage Qty","Planned Cost (INR)","Actual OCI Cost (INR)","OCI Delta (INR)","Overage @ RFP (INR)","Status"];
      const detailRows = summary.months.flatMap(m=>m.rows.map(r=>[m.month,r.service_name?.split(" – ")[0]||"",r.service_name,r.unit,r.rfp_price,r.planned_qty,r.actual_qty,r.overage_qty,r.planned_cost,r.actual_cost,r.cost_delta_oci,r.overage_cost_rfp,r.status]));
      const svcMap = {};
      summary.months.forEach(m=>m.rows.forEach(r=>{
        const key = r.service_name;
        if(!svcMap[key]) svcMap[key]={svc:r.service_name?.split(" – ")[0]||"",name:r.service_name,unit:r.unit,price:r.rfp_price,planned_qty:0,actual_qty:0,overage_qty:0,planned_cost:0,actual_cost:0,oci_delta:0,overage_rfp:0};
        svcMap[key].planned_qty+=r.planned_qty; svcMap[key].actual_qty+=r.actual_qty;
        svcMap[key].overage_qty+=r.overage_qty; svcMap[key].planned_cost+=r.planned_cost;
        svcMap[key].actual_cost+=r.actual_cost; svcMap[key].oci_delta+=r.cost_delta_oci;
        svcMap[key].overage_rfp+=r.overage_cost_rfp;
      }));
      detailRows.push(["","","","","","","","","","","","",""]);
      detailRows.push(["── SERVICE TOTALS ──","","","","","","","","","","","",""]);
      Object.values(svcMap).forEach(s=>detailRows.push([`TOTAL — ${s.svc}`,s.svc,s.name,s.unit,s.price,s.planned_qty,s.actual_qty,s.overage_qty,s.planned_cost,s.actual_cost,s.oci_delta,s.overage_rfp,s.overage_rfp>0?"OVER":"WITHIN"]));
      detailRows.push(["","","","","","","","","","","","",""]);
      detailRows.push(["GRAND TOTAL","ALL SERVICES","","","","","","",summary.grand_planned,summary.grand_actual,summary.grand_cost_delta,summary.grand_overage_rfp,summary.grand_overage_rfp>0?"OVER":"WITHIN"]);
      downloadCSV(`${clientName}_Full_Detail_All_Months.csv`, detailHeaders, detailRows);
    }, 500);
  };

  if (loading) return <div style={{ padding:48, textAlign:"center" }}><Spinner size={32}/></div>;
  if (error)   return <ErrorBanner message={error}/>;
  if (!summary) return <Empty icon="◈" title="No data yet" sub="Ingest monthly data to see your dashboard"/>;

  const svcTotals={}; const svcOciTotals={};
  summary.months.forEach(m=>m.rows.forEach(r=>{
    const svc=r.service_name?.split(" – ")[0]||r.service_name||"";
    svcTotals[svc]=(svcTotals[svc]||0)+r.overage_cost_rfp;
    svcOciTotals[svc]=(svcOciTotals[svc]||0)+r.cost_delta_oci;
  }));
  const topSvc=Object.entries(svcTotals).sort((a,b)=>b[1]-a[1]).filter(([,v])=>v>0);

  return (
    <div>
      {isReadonly && <ReadonlyBanner/>}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:14, marginBottom:24 }}>
        <KPICard label="Months analysed"        value={summary.months.length}                         accent="#2563EB"/>
        <KPICard label="Total planned (RFP)"    value={fmtINR(summary.grand_planned)}                accent="#16A34A"/>
        <KPICard label="Total actual (OCI)"     value={fmtINR(summary.grand_actual)}                 accent="#D97706"/>
        <KPICard label="Services over budget"   value={fmtINR(summary.grand_services_over||0)}       accent="#DC2626"/>
        <KPICard label="Net position"
          value={fmtINR(summary.grand_net_position||0)}
          accent={(summary.grand_net_position||0)>0?"#DC2626":"#16A34A"}
        />
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr", gap:16, marginBottom:20 }}>
        <Card>
          <SectionTitle>Monthly cost trend</SectionTitle>
          <Table headers={["Month","Planned","Actual OCI","Services Over","Net Position","Status"]}>
            {summary.months.map((m,i)=>(
              <TR key={m.month} i={i}>
                <TD bold>{m.month}</TD>
                <TD right color="#4B5563">{fmtINR(m.total_planned)}</TD>
                <TD right bold>{fmtINR(m.total_actual)}</TD>
                <TD right color={m.total_cost_delta>0?"#D97706":"#16A34A"}>{m.total_cost_delta>0?`+${fmtINR(m.total_cost_delta)}`:"—"}</TD>
                <TD right bold color={(m.total_net_position||0)>0?"#DC2626":(m.total_net_position||0)<0?"#16A34A":"#6B7280"}>
                  {(m.total_net_position||0)>0?`+${fmtINR(m.total_net_position||0)}`:fmtINR(m.total_net_position||0)}
                </TD>
                <TD right><Badge color={(m.total_net_position||0)>0?"red":"green"}>{(m.total_net_position||0)>0?"OVER":"UNDER"}</Badge></TD>
              </TR>
            ))}
          </Table>
        </Card>
        <Card>
          <SectionTitle>Service overage breakdown</SectionTitle>
          {topSvc.length===0 ? <Empty icon="✓" title="No overage" sub="All services within RFP plan"/>
            : topSvc.slice(0,8).map(([svc,amt])=>{
                const ociAmt=svcOciTotals[svc]||0; const maxAmt=topSvc[0][1];
                return (
                  <div key={svc} style={{ marginBottom:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:"#111827" }}>{svc}</span>
                      <div style={{ display:"flex", gap:12 }}>
                        <span style={{ fontSize:11, color:"#D97706" }}>OCI: {fmtINR(ociAmt)}</span>
                        <span style={{ fontSize:12, color:"#DC2626", fontWeight:700 }}>RFP: {fmtINR(amt)}</span>
                      </div>
                    </div>
                    <div style={{ background:"#F3F4F6", borderRadius:4, height:6 }}>
                      <div style={{ background:"#DC2626", height:"100%", width:`${(amt/maxAmt)*100}%`, borderRadius:4 }}/>
                    </div>
                  </div>
                );
              })
          }
        </Card>
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        <Btn onClick={downloadDashboard} variant="secondary">↓ Download consolidated report (2 CSVs)</Btn>
      </div>
    </div>
  );
}

function MonthlyAnalysis({ clientId, clientName }) {
  const { isReadonly } = useAuth();
  const { data:months } = useMonths(clientId);
  const [selected, setSelected] = useState("");
  const { data:analysis, loading, error } = useMonthAnalysis(clientId, selected||null);

  const monthOptions = (months||[]).map(m=>({ value:m, label:m }));

  const downloadMonthly = () => {
    if (!analysis) return;
    const headers = [
      "Month", "Service", "Description", "Unit",
      "Planned Cost (INR)", "Actual OCI Cost (INR)",
      "Service Delta (INR)", "Net Position (INR)"
    ];
    const rows = analysis.rows.map(r => [
      analysis.month,
      r.service_name?.split(" – ")[0] || "",
      r.service_name,
      r.unit,
      r.planned_cost,
      r.actual_cost,
      r.cost_delta_oci,
      r.net_position || 0,
    ]);
    // Add totals row
    rows.push([
      analysis.month, "TOTAL", "", "",
      analysis.total_planned,
      analysis.total_actual,
      analysis.total_cost_delta,
      analysis.total_net_position || 0,
    ]);
    downloadCSV(`Monthly_Analysis_${analysis.month}_${clientName}.csv`, headers, rows);
  };

  // Build table headers based on readonly status
  const tableHeaders = [
    "Service", "Description", "Unit",
    "Planned Cost", "Actual OCI Cost", "Service Delta",
    "Net Position (Actual − Planned)"
  ]

  return (
    <div>
      {isReadonly && <ReadonlyBanner/>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <SectionTitle children="Monthly billing analysis" sub="Actual OCI usage vs RFP planned — per service breakdown"/>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {analysis && <Btn onClick={downloadMonthly} variant="secondary" size="sm">↓ Download CSV</Btn>}
          <Select label="" options={[{value:"",label:"Select month…"}, ...monthOptions]} value={selected} onChange={e=>setSelected(e.target.value)} style={{ minWidth:160 }}/>
        </div>
      </div>

      {!selected && <Empty icon="◈" title="Select a month" sub="Choose a month from the dropdown"/>}
      {selected && loading && <div style={{ textAlign:"center", padding:48 }}><Spinner size={28}/></div>}
      {selected && error   && <ErrorBanner message={error}/>}

      {analysis && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
            <KPICard label="Planned (RFP)"       value={fmtINR(analysis.total_planned)}        accent="#2563EB"/>
            <KPICard label="Actual OCI cost"     value={fmtINR(analysis.total_actual)}         accent="#D97706"/>
            <KPICard label="Services over budget" value={fmtINR(analysis.total_services_over || 0)} accent="#DC2626"/>
            <KPICard label="Net position"
              value={fmtINR(analysis.total_net_position || 0)}
              accent={(analysis.total_net_position||0) > 0 ? "#DC2626" : "#16A34A"}
            />
          </div>

          <Table headers={tableHeaders} style={{ marginBottom:20 }}>
            {analysis.rows.map((r,i)=>(
              <TR key={`${r.service_id}-${i}`} i={i}
                style={r.not_in_rfp ? { background:"#F8F7FF", borderLeft:"3px solid #AFA9EC" } : {}}>
                <TD bold style={{ minWidth:120 }}>{r.service_name?.split(" – ")[0]||r.service_name}</TD>
                <TD style={{ color:"#6B7280", fontSize:12, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis" }}>
                  {r.not_in_rfp
                    ? <span style={{ color:"#7F77DD", fontStyle:"italic", fontSize:11 }}>Not in RFP</span>
                    : r.service_name}
                </TD>
                <TD style={{ color:"#9CA3AF", fontSize:12 }}>{r.unit}</TD>
                <TD right color="#4B5563">{r.not_in_rfp ? <span style={{color:"#AFA9EC"}}>—</span> : fmtINR(r.planned_cost)}</TD>
                <TD right bold>{fmtINR(r.actual_cost)}</TD>
                <TD right color={r.cost_delta_oci>0?"#D97706":"#16A34A"}>
                  {r.cost_delta_oci>0?`+${fmtINR(r.cost_delta_oci)}`:"—"}
                </TD>
                <TD right>
                  {r.not_in_rfp
                    ? <Badge color="purple">NOT IN RFP</Badge>
                    : <Badge color={r.status==="OVER"?"red":"green"}>{r.status}</Badge>}
                </TD>
              </TR>
            ))}
          </Table>
        </>
      )}
    </div>
  );
}

function DataIngestion({ clientId }) {
  const [step, setStep]           = useState(1);
  const [month, setMonth]         = useState("");
  const [usageFile, setUsageFile] = useState(null);
  const [costFile, setCostFile]   = useState(null);
  const [result, setResult]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [error, setError]         = useState("");
  const usageRef = useRef(); const costRef = useRef();

  const submit = async () => {
    if (!month||!usageFile||!costFile) { setError("Please fill all fields and upload both files"); return; }
    setError(""); setLoading(true);
    try { const res = await api.ingest.csv(clientId, month, usageFile, costFile); setResult(res); setStep(2); }
    catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const pullFromOCI = async () => {
    if (!month) { setError("Select a month first"); return; }
    setError(""); setSyncing(true);
    try { const res = await api.sync.triggerMonth(clientId, month); setResult(res); setStep(2); }
    catch(e) { setError(e.message); }
    finally { setSyncing(false); }
  };

  return (
    <div>
      <SectionTitle children="Monthly data ingestion" sub="Upload OCI cost and usage CSVs, or auto-pull from OCI tenancy"/>
      <PrereqBanner type="oci"/>
      <ErrorBanner message={error} onDismiss={()=>setError("")}/>
      {step===1 && (
        <Card>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:24 }}>
            <Input label="Billing month" type="month"
              value={month.replace(/^(\d{4})-(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/,(_,y,m)=>{ const mo={Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12"}[m]; return `${y}-${mo}`; })}
              onChange={e=>{ const [y,m]=e.target.value.split("-"); const mo=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)-1]; setMonth(mo?`${y}-${mo}`:""); }}
            />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24 }}>
            {[{label:"Usage CSV",ref:usageRef,file:usageFile,set:setUsageFile},{label:"Cost CSV",ref:costRef,file:costFile,set:setCostFile}].map(f=>(
              <div key={f.label}>
                <div style={{ fontSize:12, fontWeight:600, color:"#374151", marginBottom:6 }}>{f.label}</div>
                <div onClick={()=>f.ref.current.click()} style={{ border:`2px dashed ${f.file?"#2563EB":"#D1D5DB"}`, background:f.file?"#EFF6FF":"#FAFAFA", borderRadius:10, padding:"20px", textAlign:"center", cursor:"pointer" }}>
                  <input ref={f.ref} type="file" accept=".csv" style={{ display:"none" }} onChange={e=>f.set(e.target.files[0])}/>
                  <div style={{ fontSize:24, marginBottom:6 }}>📄</div>
                  <div style={{ fontSize:13, color:f.file?"#1D4ED8":"#6B7280", fontWeight:f.file?600:400 }}>{f.file?f.file.name:"Click to upload CSV"}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:12, justifyContent:"flex-end" }}>
            <Btn onClick={pullFromOCI} loading={syncing} variant="secondary">⟳ Auto-pull from OCI API</Btn>
            <Btn onClick={submit} loading={loading}>Upload & process →</Btn>
          </div>
        </Card>
      )}
      {step===2 && result && (
        <Card>
          <div style={{ textAlign:"center", padding:"32px 0" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
            <div style={{ fontSize:20, fontWeight:800, color:"#111827", marginBottom:8 }}>Import successful — {result.month}</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, maxWidth:500, margin:"20px auto" }}>
              <div style={{ background:"#F0FDF4", borderRadius:8, padding:"12px 16px" }}><div style={{ fontSize:22, fontWeight:800, color:"#16A34A" }}>{result.imported??result.rows_imported??0}</div><div style={{ fontSize:12, color:"#166534" }}>rows imported</div></div>
              <div style={{ background:"#F0FDF4", borderRadius:8, padding:"12px 16px" }}><div style={{ fontSize:22, fontWeight:800, color:"#16A34A" }}>{result.updated??0}</div><div style={{ fontSize:12, color:"#166534" }}>rows updated</div></div>
              <div style={{ background:"#FEF2F2", borderRadius:8, padding:"12px 16px" }}><div style={{ fontSize:22, fontWeight:800, color:"#DC2626" }}>{result.rows_failed??0}</div><div style={{ fontSize:12, color:"#991B1B" }}>failed</div></div>
            </div>
            <Btn onClick={()=>{ setStep(1);setUsageFile(null);setCostFile(null);setMonth("");setResult(null); }}>Import another month</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

function RFPBuilder({ clientId }) {
  const { isAdmin, isReadonly, isLimited } = useAuth();
  const readOnly = isReadonly || isLimited || false;
  const { data:lines, loading, error, refetch } = useRFP(clientId);
  const { data:catData } = useCatalogue();
  const catalogue = catData||[];
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [localLines, setLocalLines] = useState(null);
  const rows = localLines??lines??[];

  const updateRow = (idx,field,val) => {
    if (readOnly) return;
    const copy=[...rows];
    copy[idx]={...copy[idx],[field]:["planned_qty","rfp_unit_price","free_tier","part_qty","instance_qty","usage_qty"].includes(field)?Number(val):val};
    setLocalLines(copy);
  };
  const removeRow = idx => { if(readOnly)return; setLocalLines(rows.filter((_,i)=>i!==idx)); };
  const addRow = () => {
    if(readOnly)return;
    const cat=catalogue[0]; if(!cat)return;
    setLocalLines([...rows,{service_id:cat.service_id,service_name:`${cat.service} – ${cat.name}`,planned_qty:1,part_qty:1,instance_qty:1,usage_qty:744,rfp_unit_price:cat.price,free_tier:cat.free_tier||0,environment:"",notes:"",sort_order:rows.length}]);
  };
  const save = async () => {
    if(readOnly)return; setSaving(true);
    try { await api.rfp.replace(clientId,rows.map((r,i)=>({...r,sort_order:i}))); setSaved(true); setLocalLines(null); await refetch(); setTimeout(()=>setSaved(false),3000); }
    catch(e){alert(e.message);}
    finally{setSaving(false);}
  };
  const totalPlanned=rows.reduce((s,r)=>{
    const pq=r.part_qty||r.planned_qty||0;
    const iq=r.instance_qty||1;
    const uq=r.usage_qty||1;
    const tq=pq*iq;
    const billable=Math.max(0,tq-(r.free_tier||0));
    const isBudget=(r.planned_qty<=1)&&(r.rfp_unit_price>1000);
    const cost=isBudget?billable*(r.rfp_unit_price||0):billable*(r.rfp_unit_price||0)*uq;
    return s+cost;
  },0);

  if (loading) return <div style={{ textAlign:"center", padding:48 }}><Spinner size={28}/></div>;
  if (error)   return <ErrorBanner message={error}/>;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <SectionTitle children="RFP configuration" sub={readOnly?"View-only — contact your administrator to make changes":"Define planned quantities and agreed prices per service"}/>
        {!readOnly && <div style={{ display:"flex", gap:10 }}><Btn onClick={addRow} variant="secondary" size="sm">+ Add service</Btn><Btn onClick={save} loading={saving}>Save RFP</Btn></div>}
      </div>
      {!!readOnly && <ReadonlyBanner/>}
      {saved && <SuccessBanner message="RFP configuration saved successfully"/>}
      <Card style={{ marginBottom:16, background:"#F0FDF4", border:"1px solid #BBF7D0" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:14, color:"#166534", fontWeight:600 }}>Total planned monthly cost (all services)</div>
          <div style={{ fontSize:24, fontWeight:800, color:"#166534" }}>{fmtINR(totalPlanned)}</div>
        </div>
      </Card>
      <Table headers={["Service","Unit","Free Tier","Part Qty","Instances","Usage Qty","RFP Price","Planned Cost","Env","Notes",...(!readOnly?[""]:[])]}>
        {rows.map((r,idx)=>{
          const cat=catalogue.find(c=>c.service_id===r.service_id);
          const pq=r.part_qty||r.planned_qty||0;const iq=r.instance_qty||1;const uq=r.usage_qty||1;const tq=pq*iq;const billable=Math.max(0,tq-(r.free_tier||0));const isBudget=(r.planned_qty<=1)&&(r.rfp_unit_price>1000);const cost=isBudget?billable*(r.rfp_unit_price||0):billable*(r.rfp_unit_price||0)*uq;
          return (
            <TR key={idx} i={idx}>
              <TD>{readOnly?<span style={{ fontSize:13, fontWeight:600 }}>{r.service_name}</span>
                :<select value={r.service_id} onChange={e=>{const c=catalogue.find(x=>x.service_id===e.target.value);if(c){const copy=[...rows];copy[idx]={...copy[idx],service_id:c.service_id,service_name:`${c.service} – ${c.name}`,rfp_unit_price:c.price,free_tier:c.free_tier||0};setLocalLines(copy);}}} style={{padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:12}}>{catalogue.map(c=><option key={c.service_id} value={c.service_id}>{c.service} – {c.name}</option>)}</select>}</TD>
              <TD style={{ color:"#9CA3AF", fontSize:12 }}>{cat?.unit||""}</TD>
              <TD right color="#16A34A" style={{ fontSize:12 }}>{r.free_tier>0?fmt(r.free_tier):"—"}</TD>
              <TD right>{readOnly?<span style={{fontWeight:600}}>{fmt(r.part_qty||r.planned_qty||0)}</span>:<input type="number" value={r.part_qty||1} onChange={e=>updateRow(idx,"part_qty",e.target.value)} style={{width:70,padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:13,textAlign:"right"}} title="Part Qty — OCPUs, GB, units"/>}</TD>
              <TD right>{readOnly?<span style={{fontWeight:600}}>{fmt(r.instance_qty||1)}</span>:<input type="number" value={r.instance_qty||1} onChange={e=>updateRow(idx,"instance_qty",e.target.value)} style={{width:70,padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:13,textAlign:"right"}} title="Instance Qty — number of VMs"/>}</TD>
              <TD right>{readOnly?<span style={{fontWeight:600}}>{fmt(r.usage_qty||1)}</span>:<input type="number" value={r.usage_qty||1} onChange={e=>updateRow(idx,"usage_qty",e.target.value)} style={{width:70,padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:13,textAlign:"right"}} title="Usage Qty — hours/month or 1"/>}</TD>
              <TD right>{readOnly?<span style={{ fontFamily:"monospace",fontSize:12 }}>{(r.rfp_unit_price||0).toFixed(4)}</span>:<input type="number" step="0.0001" value={r.rfp_unit_price} onChange={e=>updateRow(idx,"rfp_unit_price",e.target.value)} style={{width:100,padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:13,textAlign:"right"}}/>}</TD>
              <TD right bold>{fmtINR(cost)}</TD>
              <TD>{readOnly?<span style={{ fontSize:12,color:"#6B7280" }}>{r.environment||"—"}</span>:<input value={r.environment||""} onChange={e=>updateRow(idx,"environment",e.target.value)} placeholder="Prod+DR" style={{width:80,padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:12}}/>}</TD>
              <TD>{readOnly?<span style={{ fontSize:12,color:"#6B7280" }}>{r.notes||"—"}</span>:<input value={r.notes||""} onChange={e=>updateRow(idx,"notes",e.target.value)} placeholder="—" style={{width:120,padding:"4px 8px",border:"1px solid #E5E7EB",borderRadius:6,fontSize:12}}/>}</TD>
              {!readOnly && <TD><Btn onClick={()=>removeRow(idx)} variant="danger" size="sm">✕</Btn></TD>}
            </TR>
          );
        })}
      </Table>
      {rows.length===0 && <Empty icon="✎" title="No RFP lines" sub="Click '+ Add service' to start building the RFP"/>}
    </div>
  );
}

function PriceCatalogue({ clientId }) {
  const { isReadonly } = useAuth();
  const [filter, setFilter] = useState("");
  const { data, loading, error, refetch } = useCatalogue(filter||undefined);
  const [syncing, setSyncing] = useState(false);
  const catalogue=data||[]; const services=[...new Set(catalogue.map(c=>c.service_group||c.service))];
  const sync = async () => { setSyncing(true); try{await api.catalogue.sync();await refetch();}catch(e){alert(e.message);}finally{setSyncing(false);} };
  const ask  = async () => { if(!question.trim())return; setAskLoad(true); try{const r=await api.ai.ask(clientId,question);setAnswer(r.response_text);}catch(e){setAnswer(`Error: ${e.message}`);}finally{setAskLoad(false);} };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <SectionTitle children="OCI price catalogue" sub="Current rates used for RFP and overage calculations"/>
        {!isReadonly && <Btn onClick={sync} loading={syncing} variant="secondary" size="sm">⟳ Sync from OCI</Btn>}
      </div>
      {isReadonly && <ReadonlyBanner/>}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {["All",...services].map(s=><button key={s} onClick={()=>setFilter(s==="All"?"":s)} style={{padding:"5px 14px",borderRadius:20,border:"1px solid",fontSize:12,fontWeight:600,cursor:"pointer",borderColor:filter===(s==="All"?"":s)?"#111827":"#E5E7EB",background:filter===(s==="All"?"":s)?"#111827":"#fff",color:filter===(s==="All"?"":s)?"#fff":"#4B5563"}}>{s}</button>)}
      </div>
      {loading && <div style={{ textAlign:"center",padding:32 }}><Spinner/></div>}
      {!loading && <Table headers={["Service","Description","Unit","OCI Price (₹)","Free Tier","Notes"]} style={{ marginBottom:20 }}>{catalogue.map((c,i)=><TR key={c.id} i={i}><TD bold>{c.service}</TD><TD style={{maxWidth:240,overflow:"hidden",textOverflow:"ellipsis",color:"#4B5563"}}>{c.name}</TD><TD style={{color:"#9CA3AF",fontSize:12}}>{c.unit}</TD><TD right bold style={{fontFamily:"monospace"}}>₹{Number(c.price).toFixed(4)}</TD><TD right>{c.free_tier>0?<Badge color="green">{fmt(c.free_tier)} {c.unit} free</Badge>:<span style={{color:"#D1D5DB"}}>—</span>}</TD><TD style={{color:"#9CA3AF",fontSize:12}}>{c.tier_note||"—"}</TD></TR>)}</Table>}
    </div>
  );
}

function AdminGuard() {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center" }}><Spinner size={32}/></div>;
  if (!user) return <Login/>;
  if (!user.is_admin) return <div style={{ padding:48,textAlign:"center",fontSize:18,color:"#DC2626" }}>Access denied — admin only</div>;
  return <AdminPanel/>;
}

function AppInner() {
  const { user, loading } = useAuth();
  const [view, setView]   = useState("dashboard");
  const [clientId, setClientId] = useState(null);
  const { data:clients } = useClients(user?.id);

  useEffect(() => {
    if (clients?.length && !clientId) setClientId(clients[0].id);
  }, [clients]);

  const client = clients?.find(c=>c.id===clientId);

  if (loading) return <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0F172A" }}><Spinner size={40} color="#3B82F6"/></div>;
  if (!user) return <Login/>;

  const views = { dashboard:Dashboard, analysis:MonthlyAnalysis, ingest:DataIngestion, rfp:RFPBuilderV3, alerts:AlertConfig, compartments:CompartmentAnalysis, settings:ClientSettings, guide:OnboardingGuide, catalogue:PriceCatalogue };
  const ViewComponent = views[view]||Dashboard;

  return (
    <div style={{ display:"flex", height:"100vh", fontFamily:"'DM Sans','Segoe UI',sans-serif", background:"#F3F4F6", fontSize:14 }}>
      <Sidebar view={view} setView={setView} clients={clients||[]} activeClientId={clientId} setActiveClientId={id=>{ setClientId(id); setView("dashboard"); }}/>
      <div style={{ flex:1, overflowY:"auto" }}>
        {!clientId
          ? <div style={{ padding:"80px 48px",textAlign:"center" }}><div style={{ fontSize:48,marginBottom:16 }}>◎</div><div style={{ fontSize:20,fontWeight:700,color:"#111827",marginBottom:8 }}>No client selected</div><div style={{ color:"#6B7280",fontSize:14 }}>Add a client using the sidebar to get started</div></div>
          : <div style={{ padding:"28px 32px",maxWidth:1200 }}><div style={{ marginBottom:20 }}><div style={{ fontSize:13,color:"#9CA3AF" }}>{client?.name} · {view.charAt(0).toUpperCase()+view.slice(1)}</div></div><ViewComponent clientId={clientId} clientName={client?.name||""}/></div>
        }
      </div>
    </div>
  );
}

export default function App() {
  if (window.location.pathname === "/guide") {
    return <OnboardingGuide/>;
  }
  if (window.location.pathname==="/admin") {
    return <AuthProvider><AdminGuard/></AuthProvider>;
  }
  return <AuthProvider><AppInner/></AuthProvider>;
}
